import { toIterable } from '../common/index';
import { isInvisiblySmallElement } from './sizeHelpers';
import type {
  ElementsMapOrArray,
  ExcalidrawElement,
  NonDeleted,
  NonDeletedExcalidrawElement,
} from './types';

/**
 * @deprecated unsafe, use hashElementsVersion instead
 */
export const getSceneVersion = (elements: readonly ExcalidrawElement[]) =>
  elements.reduce((acc, el) => acc + el.version, 0);

/**
 * Hashes elements' versionNonce (using djb2 algo). Order of elements matters.
 */
export const hashElementsVersion = (elements: ElementsMapOrArray): number => {
  let hash = 5381;
  for (const element of toIterable(elements)) {
    hash = (hash << 5) + hash + element.versionNonce;
  }
  return hash >>> 0; // Ensure unsigned 32-bit integer
};

// string hash function (using djb2). Not cryptographically secure, use only
// for versioning and such.
// note: hashes individual code units (not code points),
// but for hashing purposes this is fine as it iterates through every code unit
// (as such, no need to encode to byte string first)
export const hashString = (s: string): number => {
  let hash: number = 5381;
  for (let i = 0; i < s.length; i++) {
    const char: number = s.charCodeAt(i);
    hash = (hash << 5) + hash + char;
  }
  return hash >>> 0; // Ensure unsigned 32-bit integer
};

export const isNonDeletedElement = <T extends ExcalidrawElement>(
  element: T
): element is NonDeleted<T> => !element.isDeleted;

export const getVisibleElements = (
  elements: readonly ExcalidrawElement[]
): readonly NonDeletedExcalidrawElement[] =>
  elements.filter(
    (el): el is NonDeletedExcalidrawElement =>
      isNonDeletedElement(el) && !isInvisiblySmallElement(el)
  );

export const getNonDeletedElements = <T extends ExcalidrawElement>(
  elements: readonly T[]
): readonly NonDeleted<T>[] => elements.filter(isNonDeletedElement);

export * from './align';
export * from './arrange';
export * from './arrowEndpointText';
export * from './arrowheads';
export * from './arrows/focus';
export * from './arrows/helpers';
export * from './binding';
export * from './bounds';
export * from './bucketFill';
export * from './collision';
export * from './comparisons';
export * from './containerCache';
export * from './convertToShape';
export * from './cropElement';
export * from './delta';
export * from './distance';
export * from './distribute';
export * from './dragElements';
export * from './duplicate';
export * from './elbowArrow';
export * from './elementLink';
export * from './embeddable';
export * from './flowchart';
export * from './fractionalIndex';
export * from './frame';
export * from './groups';
export * from './heading';
export * from './image';
export * from './linearElementEditor';
export * from './listMarker';
export * from './mutateElement';
export * from './newElement';
export * from './positionElementsOnGrid';
export * from './renderElement';
export * from './resizeElements';
export * from './resizeTest';
export * from './Scene';
export * from './selection';
export * from './shape';
export * from './shapeEffects';
export * from './showSelectedShapeActions';
export * from './sizeHelpers';
export * from './sortElements';
export * from './stickyNote';
export * from './store';
export * from './strokeGeometry';
export * from './textElement';
export * from './textLayout';
export * from './textMeasurements';
export * from './textWrapping';
export * from './transform';
export * from './transformHandles';
export * from './typeChecks';
export * from './utils';
export * from './zindex';
